import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCheck, Download, MessageCircle, Printer, Send } from 'lucide-react';
import type { Invoice, OrderWithItems } from '@tailoring-crm/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@tailoring-crm/ui';
import { useInvoiceDownload, useSendInvoice } from '../../lib/invoices/use-invoices.js';
import { useShareInvoiceByHand } from '../../lib/invoices/use-share-invoice.js';
import { useOrderMessages } from '../../lib/notifications/use-notifications.js';
import { formatCurrency } from '../../lib/format.js';

/**
 * The moment after "Book the order", when the bill was issued in the same breath.
 *
 * The customer is still at the counter, so this is where the bill goes to them — not on the next
 * screen, after somebody has found the invoice card. Relay sends it as a document when the shop
 * has connected WhatsApp; the shop's own phone can always send it, with the PDF downloaded to
 * attach. Closing the dialog, however it is closed, lands on the order.
 */
export function BookingDoneDialog({
  order,
  invoice,
  onClose,
}: {
  order: OrderWithItems;
  invoice: Invoice;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const messages = useOrderMessages(order.id);
  const send = useSendInvoice();
  const download = useInvoiceDownload();
  const byHand = useShareInvoiceByHand();
  const [sent, setSent] = useState(false);

  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  const onSend = async (): Promise<void> => {
    try {
      await send.mutateAsync({ invoiceId: invoice.id });
      setSent(true);
      toast({ title: t('invoices.sent'), variant: 'success' });
    } catch (error) {
      report(error);
    }
  };

  const onPrint = async (): Promise<void> => {
    try {
      const link = await download.mutateAsync({ invoiceId: invoice.id, paperSize: 'thermal' });
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      report(error);
    }
  };

  const rows: [string, number][] = [
    [t('orders.total'), invoice.totals.totalPaise],
    [t('orders.paid'), invoice.paidPaise],
    [t('orders.balance'), invoice.balancePaise],
  ];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('orders.booking.done.title', { number: order.orderNumber })}</DialogTitle>
          <DialogDescription>
            {t('orders.booking.done.billIssued', { number: invoice.number })}
          </DialogDescription>
        </DialogHeader>

        <dl className="flex flex-col gap-2 text-sm">
          {rows.map(([label, paise]) => (
            <div key={label} className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="font-semibold tabular-nums">{formatCurrency(paise)}</dd>
            </div>
          ))}
        </dl>

        <div className="flex flex-col gap-2">
          {messages.data?.relayConnected ? (
            <Button disabled={send.isPending || sent} onClick={() => void onSend()}>
              {sent ? <CheckCheck aria-hidden="true" /> : <Send aria-hidden="true" />}
              {sent ? t('orders.booking.done.sent') : t('orders.booking.done.sendOnWhatsapp')}
            </Button>
          ) : null}
          <Button
            variant={messages.data?.relayConnected ? 'outline' : 'default'}
            disabled={byHand.isPending}
            onClick={() => void byHand.share(invoice).catch(report)}
          >
            <MessageCircle aria-hidden="true" />
            <Download aria-hidden="true" />
            {t('invoices.shareByHand')}
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={download.isPending} onClick={() => void onPrint()}>
            <Printer aria-hidden="true" />
            {t('orders.booking.done.print')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('orders.booking.done.openOrder')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
